import React from 'react';
import { Pressable, Text, View } from 'react-native';
import MaterialCommunityIcons from '@react-native-vector-icons/material-design-icons/static';
import { iconSize } from '../../../utils/libs';
import type { InventoryStyles } from './inventoryStyles';

type StylesProp = { styles: InventoryStyles; theme: any };

/** Selectable pill: blue with a tick when selected, used for every filter choice. */
export const InventoryChip: React.FC<
  StylesProp & {
    label: string;
    count?: number | string | null;
    selected: boolean;
    onPress: () => void;
  }
> = ({ styles, theme, label, count, selected, onPress }) => (
  <Pressable
    onPress={onPress}
    accessibilityRole="button"
    accessibilityState={{ selected }}
    style={({ pressed }) => [
      styles.chip,
      selected && styles.chipSelected,
      pressed && styles.toolButtonActive,
    ]}>
    {selected ? (
      <MaterialCommunityIcons
        name="check"
        size={iconSize(16)}
        color={theme.colors.primaryDark}
      />
    ) : null}
    <Text style={[styles.chipText, selected && styles.chipTextSelected]}>
      {label}
    </Text>
    {count != null ? (
      <Text style={[styles.chipCount, selected && styles.chipCountSelected]}>
        {String(count)}
      </Text>
    ) : null}
  </Pressable>
);

export const InventoryButton: React.FC<
  StylesProp & {
    label: string;
    onPress: () => void;
    variant?: 'primary' | 'ghost';
    icon?: string;
    /** After the label: a chevron for a button that moves on ("Show 12 products"). */
    trailingIcon?: string;
    disabled?: boolean;
    style?: any;
  }
> = ({
  styles,
  theme,
  label,
  onPress,
  variant = 'primary',
  icon,
  trailingIcon,
  disabled,
  style,
}) => {
  const primary = variant === 'primary';
  const iconColor = primary ? theme.colors.textInverse : theme.colors.text;
  return (
    <Pressable
      onPress={onPress}
      disabled={disabled}
      accessibilityRole="button"
      style={({ pressed }) => [
        styles.button,
        primary ? styles.buttonPrimary : styles.buttonGhost,
        pressed && styles.buttonPressed,
        disabled && styles.buttonDisabled,
        style,
      ]}>
      {icon ? (
        <MaterialCommunityIcons
          name={icon as any}
          size={iconSize(18)}
          color={iconColor}
        />
      ) : null}
      <Text style={[styles.buttonText, primary && styles.buttonTextPrimary]}>
        {label}
      </Text>
      {trailingIcon ? (
        <MaterialCommunityIcons
          name={trailingIcon as any}
          size={iconSize(20)}
          color={iconColor}
          style={styles.buttonTrailingIcon}
        />
      ) : null}
    </Pressable>
  );
};

export const InventoryTextLink: React.FC<
  StylesProp & { label: string; onPress: () => void; chevron?: boolean }
> = ({ styles, theme, label, onPress, chevron }) => (
  <Pressable
    onPress={onPress}
    accessibilityRole="button"
    style={({ pressed }) => [
      styles.textLink,
      { flexDirection: 'row', alignItems: 'center' },
      pressed && styles.buttonPressed,
    ]}>
    <Text style={styles.textLinkText}>{label}</Text>
    {chevron ? (
      <MaterialCommunityIcons
        name="chevron-right"
        size={iconSize(18)}
        color={theme.colors.primaryDark}
      />
    ) : null}
  </Pressable>
);

/** Icon, title, one sentence and an action, inside the table card. */
export const InventoryEmptyState: React.FC<
  StylesProp & {
    icon: string;
    title: string;
    message: string;
    primary?: { label: string; onPress: () => void; icon?: string };
    secondary?: { label: string; onPress: () => void };
    /** Ghost instead of filled primary button. */
    ghost?: boolean;
    /** Keeps the Add Product button green, as the list has always shown it. */
    primaryColor?: string;
  }
> = ({
  styles,
  theme,
  icon,
  title,
  message,
  primary,
  secondary,
  ghost,
  primaryColor,
}) => (
  <View style={styles.empty}>
    <View style={styles.emptyIcon}>
      <MaterialCommunityIcons
        name={icon as any}
        size={iconSize(28)}
        color={theme.colors.textGray600}
      />
    </View>
    <Text style={styles.emptyTitle}>{title}</Text>
    <Text style={styles.emptyText}>{message}</Text>
    {primary || secondary ? (
      <View style={styles.emptyActions}>
        {primary ? (
          <InventoryButton
            styles={styles}
            theme={theme}
            label={primary.label}
            icon={primary.icon}
            onPress={primary.onPress}
            variant={ghost ? 'ghost' : 'primary'}
            style={primaryColor ? { backgroundColor: primaryColor } : undefined}
          />
        ) : null}
        {secondary ? (
          <InventoryTextLink
            styles={styles}
            theme={theme}
            label={secondary.label}
            onPress={secondary.onPress}
          />
        ) : null}
      </View>
    ) : null}
  </View>
);
